import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FaCheck, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import apiClient from '../../api';

// Imports dos seus componentes existentes
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Button';
import { FormGroup, Label, Input, Message } from '../../components/common/Forms';
import { AuthPageContainer, AuthFormCard, Title, SubtitleLink } from '../../components/common/AuthLayout';

// --- Styled Components Trazidos do Cadastro ---

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 40px;
    ${props => props.hasError && css`
      border-color: #dc3545 !important;
      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
      }
    `}
    ${props => props.isValid && !props.hasError && css`
      border-color: #28a745 !important;
    `}
  }
`;

const IconContainer = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  color: ${props => props.color || '#666'};
  
  ${props => props.isButton && css`
    cursor: pointer;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
  `}
`;

const ErrorText = styled.small`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ChecklistContainer = styled.div`
  margin-top: 0.5rem;
  background-color: #f9f9f9;
  padding: 0.8rem;
  border-radius: 6px;
  border: 1px solid #eee;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  color: ${props => props.isValid ? '#28a745' : '#dc3545'}; 
  transition: color 0.2s ease;

  svg { font-size: 0.7rem; }
  &:last-child { margin-bottom: 0; }
`;

// --- Componente Principal ---

function ResetarSenhaPage() {
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mensagem, setMensagem] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Controle de visibilidade da senha
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { token } = useParams();
  const navigate = useNavigate();

  const MAX_PASSWORD_LENGTH = 32;

  // Lógica de Validação (A mesma do cadastro)
  const passwordValidations = useMemo(() => {
    return {
      length: senha.length >= 8 && senha.length <= MAX_PASSWORD_LENGTH,
      hasLetter: /[a-zA-Z]/.test(senha),
      hasNumber: /[0-9]/.test(senha),
      hasSpecial: /[!@#$%^&*()_+\-=$$$${};':"\\|,.<>\/?]/.test(senha)
    };
  }, [senha]);

  const isPasswordValid = Object.values(passwordValidations).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensagem({ text: '', type: '' });

    // Validações antes de enviar
    if (!isPasswordValid) {
      setMensagem({ text: 'A senha não atende aos requisitos de segurança.', type: 'error' });
      return;
    }

    if (senha !== confirmarSenha) {
      setMensagem({ text: 'As senhas não conferem.', type: 'error' });
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await apiClient.post('/api/resetar-senha', {
        token,
        senha,
        confirmar_senha: confirmarSenha
      });
      
      setMensagem({ text: response.data.message, type: 'success' });
      setIsSuccess(true);

      // Limpeza preventiva (Logout no frontend)
      localStorage.removeItem('user');

      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      setMensagem({
        text: error.response?.data?.error || 'Erro ao redefinir senha.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <AuthPageContainer>
        <AuthFormCard>
          <Title>Crie sua Nova Senha</Title>
          <form onSubmit={handleSubmit}>
            
            {/* Campo Nova Senha */}
            <FormGroup>
              <Label htmlFor="senha">Nova Senha:</Label>
              <InputWrapper hasError={senha.length > 0 && !isPasswordValid} isValid={isPasswordValid}>
                <Input 
                  id="senha" 
                  type={showPassword ? "text" : "password"} 
                  value={senha} 
                  onChange={e => setSenha(e.target.value)} 
                  maxLength={MAX_PASSWORD_LENGTH}
                  required
                  disabled={isLoading || isSuccess}
                />
                <IconContainer isButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconContainer>
              </InputWrapper>

              {/* Checklist Visual */}
              <ChecklistContainer>
                <ChecklistItem isValid={passwordValidations.length}>
                  {passwordValidations.length ? <FaCheck /> : <FaTimes />} 8 a {MAX_PASSWORD_LENGTH} caracteres
                </ChecklistItem>
                <ChecklistItem isValid={passwordValidations.hasLetter}>
                  {passwordValidations.hasLetter ? <FaCheck /> : <FaTimes />} Pelo menos uma letra
                </ChecklistItem>
                <ChecklistItem isValid={passwordValidations.hasNumber}>
                  {passwordValidations.hasNumber ? <FaCheck /> : <FaTimes />} Pelo menos um número
                </ChecklistItem>
                <ChecklistItem isValid={passwordValidations.hasSpecial}>
                  {passwordValidations.hasSpecial ? <FaCheck /> : <FaTimes />} Um caractere especial (!@#$...)
                </ChecklistItem>
              </ChecklistContainer>
            </FormGroup>

            {/* Campo Confirmar Senha */}
            <FormGroup>
              <Label htmlFor="confirmarSenha">Confirme a Nova Senha:</Label>
              <InputWrapper 
                hasError={confirmarSenha.length > 0 && senha !== confirmarSenha} 
                isValid={confirmarSenha.length > 0 && senha === confirmarSenha}
              >
                <Input 
                  id="confirmarSenha"
                  type={showConfirmPassword ? "text" : "password"} 
                  value={confirmarSenha} 
                  onChange={e => setConfirmarSenha(e.target.value)} 
                  maxLength={MAX_PASSWORD_LENGTH}
                  required 
                  disabled={isLoading || isSuccess}
                />
                <IconContainer isButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </IconContainer>
              </InputWrapper>
              {confirmarSenha.length > 0 && senha !== confirmarSenha && (
                <ErrorText>As senhas não conferem.</ErrorText>
              )}
            </FormGroup>

            {mensagem.text && 
              <Message type={mensagem.type} style={{marginBottom: '1rem'}}>
                {mensagem.text}
              </Message>
            }

            <Button type="submit" style={{ width: '100%' }} disabled={isLoading || isSuccess}>
              {isLoading ? 'Salvando...' : 'Redefinir Senha'}
            </Button>
          </form>

          {!isSuccess && (
            <SubtitleLink>
              <Link to="/login">Voltar ao Login</Link>
            </SubtitleLink>
          )}
        </AuthFormCard>
      </AuthPageContainer>
    </>
  );
}

export default ResetarSenhaPage;